import React, { FC, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { eachDayOfInterval } from "date-fns";
import ReactGA from "react-ga4";
import { track, roomItem, CURRENCY } from "utils/analytics/events";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import NomadDatePicker from "shared/DateField/NomadDatePicker";
import { currencySymbol } from "shared/CurrencySymbol";
import successHandler from "utils/helpers/SuccessHandler";
import ErrorHandler from "utils/helpers/ErrorHandler";
import { isAuthenticated } from "utils/helpers/CommonHelper";
import {
  reserveBooking,
  checkDuplicateBooking,
} from "redux/reducers/Payments/PaymentReducer";
import { AppDispatch, RootState } from "redux/store";



const DATE_FORMAT = "YYYY-MM-DD";
const MIN_STAY_DAYS = 30;
const MAX_EXTRA_GUESTS_CAP = 15;

interface CardRoom {
  id: number;
  name: string;
  max_guests?: number | null;
  price_per_month: string;
  booked_dates: [string, string][];
  availability_start?: string | null;
  availability_end?: string | null;
  next_available_date?: string | null;
}

interface CardProperty {
  slug: string;
  title: string;
  instant_booking: boolean;
  minimum_months_of_booking?: number | null;
  allow_extra_guest?: boolean;
  per_extra_guest_amount?: number | string | null;
  service_fee: number;
  availability_start?: string | null;
  availability_end?: string | null;
}

interface RoomBookingCardProps {
  room: CardRoom;
  property: CardProperty;
  checkIn: string | null;
  checkOut: string | null;
  onDatesChange: (checkIn: string | null, checkOut: string | null) => void;
}

const toStr = (value: any): string | null =>
  value ? moment(value).format(DATE_FORMAT) : null;

/**
 * Same rules as the listing page sidebar: a booking needs at least 30 days,
 * so any free gap shorter than 30 days between bookings (or before the first
 * booking / after the last one) is not selectable.
 */
const computeAvailability = (room: CardRoom, property: CardProperty) => {
  const today = moment().startOf("day");
  const bookings = (room.booked_dates || [])
    .filter(([s, e]) => s && e)
    .sort((a, b) => moment(a[0]).diff(moment(b[0])));

  let start = room.next_available_date
    ? moment(room.next_available_date)
    : moment(
        room.availability_start || property.availability_start || undefined
      );
  if (start.isBefore(today)) start = today.clone();

  const endSource = room.availability_end || property.availability_end;
  const end = endSource ? moment(endSource) : null;

  const blocked = new Set<string>();
  bookings.forEach(([s, e]) => {
    eachDayOfInterval({
      start: moment(s).toDate(),
      end: moment(e).toDate(),
    }).forEach((day) => blocked.add(moment(day).format(DATE_FORMAT)));
  });

  // No end date at all means open-ended availability: nothing else to check.
  if (!end || !end.isValid()) {
    return { blocked, hasWindow: true };
  }

  const blockRange = (from: moment.Moment, toExclusive: moment.Moment, inclusive = false) => {
    const cursor = from.clone();
    while (
      inclusive ? cursor.isSameOrBefore(toExclusive) : cursor.isBefore(toExclusive)
    ) {
      blocked.add(cursor.format(DATE_FORMAT));
      cursor.add(1, "day");
    }
  };

  let hasWindow = false;

  if (bookings.length === 0) {
    const total = end.diff(start, "days");
    hasWindow = total >= MIN_STAY_DAYS;
    if (!hasWindow) blockRange(start, end, true);
    return { blocked, hasWindow };
  }

  const firstStart = moment(bookings[0][0]);
  const before = firstStart.diff(start, "days");
  if (before >= MIN_STAY_DAYS) hasWindow = true;
  else if (before > 0) blockRange(start, firstStart);

  for (let i = 0; i < bookings.length - 1; i++) {
    const currentEnd = moment(bookings[i][1]);
    const nextStart = moment(bookings[i + 1][0]);
    const gap = nextStart.diff(currentEnd, "days") - 1;
    if (gap >= MIN_STAY_DAYS) hasWindow = true;
    else if (gap > 0) blockRange(currentEnd.clone().add(1, "day"), nextStart);
  }

  const lastEnd = moment(bookings[bookings.length - 1][1]);
  const after = end.diff(lastEnd, "days") - 1;
  if (after >= MIN_STAY_DAYS) hasWindow = true;
  else if (after > 0) blockRange(lastEnd.clone().add(1, "day"), end, true);

  return { blocked, hasWindow };
};

const RoomBookingCard: FC<RoomBookingCardProps> = ({
  room,
  property,
  checkIn,
  checkOut,
  onDatesChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state: RootState) => state.user.currentUser);

  const [guests, setGuests] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [duplicate, setDuplicate] = useState<{ number: string } | null>(null);

  const { blocked, hasWindow } = useMemo(
    () => computeAvailability(room, property),
    [room, property]
  );

  const today = moment().startOf("day");
  const nextAvailable = room.next_available_date
    ? moment(room.next_available_date)
    : null;
  const startSource = room.availability_start || property.availability_start;
  let minDate = today.toDate();
  if (nextAvailable && nextAvailable.isAfter(today)) {
    minDate = nextAvailable.toDate();
  } else if (startSource && moment(startSource).isAfter(today)) {
    minDate = moment(startSource).toDate();
  }
  const endSource = room.availability_end || property.availability_end;
  const maxDate = endSource ? moment(endSource).toDate() : undefined;

  const isBlocked = (date: Date) => blocked.has(moment(date).format(DATE_FORMAT));

  // Money
  const rent = Number(room.price_per_month) || 0;
  const includedGuests = Number(room.max_guests) || 1;
  const allowExtra = Boolean(property.allow_extra_guest);
  const maxGuests = allowExtra ? MAX_EXTRA_GUESTS_CAP : includedGuests;
  const extraGuests = allowExtra && guests > includedGuests ? guests - includedGuests : 0;
  const extraAmount = extraGuests * (parseFloat(String(property.per_extra_guest_amount || 0)) || 0);
  const subtotal = rent + extraAmount;
  const serviceFee = (subtotal * (property.service_fee || 0)) / 100;
  const total = subtotal + serviceFee;
  const money = (amount: number) => `${currencySymbol()}${amount.toFixed(2)}`;

  // Stay checks
  const months =
    checkIn && checkOut
      ? Math.max(0, Math.ceil(moment(checkOut).diff(moment(checkIn), "months", true)))
      : 0;
  const minMonths = property.minimum_months_of_booking || 1;
  const belowMinimum = months > 0 && months < minMonths;
  const conflict = Boolean(
    checkIn &&
      checkOut &&
      (room.booked_dates || []).some(
        ([s, e]) => s && e && moment(s).isBefore(checkOut) && moment(e).isAfter(checkIn)
      )
  );
  const showPricing = Boolean(checkIn && checkOut && !conflict && rent > 0);

  const handleCheckIn = (value: any) => {
    const next = toStr(value);
    const keepOut =
      next && checkOut && moment(checkOut).isSameOrAfter(moment(next).add(MIN_STAY_DAYS, "days"));
    onDatesChange(next, keepOut ? checkOut : null);
  };

  const handleCheckOut = (value: any) => onDatesChange(checkIn, toStr(value));

  // Warn (and block) if this guest already asked for these dates.
  useEffect(() => {
    if (!checkIn || !checkOut || !isAuthenticated()) {
      setDuplicate(null);
      return;
    }
    let cancelled = false;
    dispatch(
      checkDuplicateBooking({
        propertyId: property.slug,
        roomId: room.id,
        checkIn,
        checkOut,
      }) as any
    ).then((action: any) => {
      if (cancelled) return;
      setDuplicate(
        action?.payload?.has_booking
          ? { number: action.payload.booking_number }
          : null
      );
    });
    return () => {
      cancelled = true;
    };
  }, [checkIn, checkOut, room.id, property.slug, dispatch]);

  const loggedIn = isAuthenticated();
  const canSubmit =
    Boolean(checkIn && checkOut) && !conflict && hasWindow && !duplicate && !submitting;

  const handleReserve = async () => {
    if (!checkIn || !checkOut || !canSubmit) return;

    const source = location.pathname.startsWith("/h/") ? "house_page" : "room_page";
    const eventItem = roomItem(room, property);
    track("begin_checkout", {
    currency: CURRENCY,
    value: total,
    source,
    items: [eventItem],
    });

    // 1. Not logged in: go to login and come back to this same link with the dates.
    if (!loggedIn) {
      const from = `${location.pathname}?in=${checkIn}&out=${checkOut}`;
      track("login_required", { source, property_slug: property.slug });
      navigate("/login", { state: { from } });
      return;
    }

    // 2. Logged in but account not ready.
    if (!currentUser) {
      ErrorHandler("We're still loading your account. Try again in a moment.");
      return;
    }
    if (!currentUser.profile_complete) {
      setProfileIncomplete(true);
      return;
    }
    setProfileIncomplete(false);

    // 3. Send the request.
    setSubmitting(true);
    try {
      const booking = {
        number_of_guests: guests,
        service_fee_amount: parseFloat(serviceFee.toFixed(2)),
        property_id: property.slug,
        extra_guest_amount: parseFloat(extraAmount.toFixed(2)),
        check_in_date: checkIn,
        check_out_date: checkOut,
        no_of_months: Math.max(1, months),
        line_items_attributes: [
          {
            room_id: room.id,
            price: room.price_per_month,
            quantity: 1,
            property_id: property.slug,
          },
        ],
      };
      const action: any = await dispatch(reserveBooking({ booking } as any));

      if (action?.type === "payment/reserveBooking/fulfilled") {
        ReactGA.event({
          category: "Booking",
          action: "booking_initiated",
          label: property.slug,
          value: total,
        });
        track("booking_request_submitted", {
            currency: CURRENCY,
            value: total,
            source,
            months: Math.max(1, months),
            items: [eventItem],
        });
        successHandler(
          "Booking request sent! The host will confirm shortly. Check your email for updates.",
          6000
        );
        
        navigate("/manage_bookings");
      } else {
        throw new Error(
          typeof action?.payload === "string" ? action.payload : "Booking failed"
        );
      }
    } catch (error) {
      ErrorHandler(
        error instanceof Error
          ? error.message
          : "Failed to send your request. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const labelClass ="text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1 block";

  // Forces readable text inside the date pickers in dark mode (value, placeholder, icons).
  const dateFieldClass =
    "dark:[&_input]:!text-neutral-100 dark:[&_button]:!text-neutral-100 dark:[&_[data-placeholder]]:!text-neutral-400 dark:[&_input::placeholder]:!text-neutral-400 dark:[&_svg]:!text-neutral-300";

  return (
    <div
      id="booking-card"
      className="lg:sticky lg:top-28 rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-sm overflow-hidden text-neutral-900 dark:text-neutral-100"
    >
      <div className="p-5 border-b border-neutral-200 dark:border-neutral-700">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-semibold text-neutral-900 dark:text-white">{money(rent)}</span>
          <span className="text-sm text-neutral-500 dark:text-neutral-400">/ month</span>
        </div>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
          {room.name} · {property.title}
        </p>
        {!hasWindow && (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400">
            No booking windows of 30+ days are open for this room.
          </p>
        )}
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className={labelClass}>Check-in</label>
            <div className={dateFieldClass}>
            <NomadDatePicker
              placeholder="Add date"
              disabled={!hasWindow}
              value={checkIn ? moment(checkIn).toDate() : null}
              onChange={handleCheckIn}
              minDate={minDate}
              maxDate={maxDate}
              excludeDate={isBlocked}
            />
            </div>
          </div>
          <div>
            <label className={labelClass}>Check-out</label>
            <div className={dateFieldClass}>
            <NomadDatePicker
              placeholder="Add date"
              disabled={!hasWindow || !checkIn}
              value={checkOut ? moment(checkOut).toDate() : null}
              onChange={handleCheckOut}
              minDate={
                checkIn ? moment(checkIn).add(MIN_STAY_DAYS, "days").toDate() : minDate
              }
              maxDate={maxDate}
              excludeDate={isBlocked}
            />
            </div>
          </div>
        </div>

        {nextAvailable && hasWindow && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Next available: {nextAvailable.format("MMM DD, YYYY")}
          </p>
        )}

        {conflict && (
          <div className="px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            Some of these dates are already booked. Try different dates.
          </div>
        )}

        {months > 0 && !conflict && (
          <div
            className={`px-3 py-2.5 rounded-lg border text-xs ${
              belowMinimum
                ? "bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300"
                : "bg-green-50 border-green-200 text-green-800 dark:bg-green-900/10 dark:border-green-800 dark:text-green-300"
            }`}
          >
            {belowMinimum ? (
              <>Below the {minMonths}-month minimum, but you're welcome to send a request.</>
            ) : (
              <>
                <strong>
                  {months} {months === 1 ? "month" : "months"}
                </strong>{" "}
                · meets the minimum stay.
              </>
            )}
          </div>
        )}

        {maxGuests > 1 && (
          <div>
            <label className={labelClass}>Guests</label>
            <div className="flex items-center justify-between rounded-xl border border-neutral-200 dark:border-neutral-700 px-3 py-2">
              <span className="text-sm">
                {guests} {guests === 1 ? "guest" : "guests"}
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label="Remove a guest"
                  disabled={guests <= 1}
                  onClick={() => setGuests((g) => Math.max(1, g - 1))}
                  className="w-8 h-8 rounded-full border border-neutral-300 dark:border-neutral-600 flex items-center justify-center disabled:opacity-40"
                >
                  <i className="las la-minus"></i>
                </button>
                <button
                  type="button"
                  aria-label="Add a guest"
                  disabled={guests >= maxGuests}
                  onClick={() => setGuests((g) => Math.min(maxGuests, g + 1))}
                  className="w-8 h-8 rounded-full border border-neutral-300 dark:border-neutral-600 flex items-center justify-center disabled:opacity-40"
                >
                  <i className="las la-plus"></i>
                </button>
              </div>
            </div>
            {extraGuests > 0 && (
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Room includes {includedGuests}. Extra guests are charged per month.
              </p>
            )}
          </div>
        )}

        {showPricing && (
          <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700 space-y-2 text-sm text-neutral-600 dark:text-neutral-300">
            <div className="flex justify-between">
              <span>Monthly rent</span>
              <span>{money(rent)}</span>
            </div>
            {extraGuests > 0 && (
              <div className="flex justify-between">
                <span>Extra guest ({extraGuests})</span>
                <span>{money(extraAmount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>One-time service fee</span>
              <span>{money(serviceFee)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-neutral-200 dark:border-neutral-700 font-semibold text-base text-neutral-900 dark:text-white">
              <span>Total</span>
              <span>{money(total)}</span>
            </div>
          </div>
        )}

        {duplicate && (
          <div className="px-3 py-2.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-xs dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300">
            You already have a request <strong>#{duplicate.number}</strong> for these dates.{" "}
            <Link to="/manage_bookings" className="underline">
              View my bookings
            </Link>
          </div>
        )}

        {profileIncomplete && (
          <div className="px-3 py-2.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-xs dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300">
            Complete your profile to send a request.{" "}
            <Link to="/account" className="underline font-medium">
              Complete profile
            </Link>
          </div>
        )}

        <ButtonPrimary
          className="w-full"
          disabled={!canSubmit}
          loading={submitting}
          onClick={handleReserve}
        >
          {!checkIn || !checkOut
            ? "Select dates"
            : duplicate
            ? "Request already sent"
            : loggedIn
            ? "Reserve"
            : "Log in to reserve"}
        </ButtonPrimary>

        {!loggedIn && checkIn && checkOut && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center">
            You'll log in or sign up, then come straight back here with your dates.
          </p>
        )}

        {showPricing && !property.instant_booking && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-green-200 bg-green-50 text-green-800 text-xs font-medium dark:bg-green-900/10 dark:border-green-800 dark:text-green-300">
            <i className="las la-shield-alt text-base"></i>
            You won't pay anything yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomBookingCard;